import { useEffect, useRef, useState } from 'react';

import useQueryStringParameters from '../../../utils/useQueryStringParameters';
import {
  buildSearchPerformanceQueryParameters,
  type SearchPerformanceQueryParams,
} from '../SearchPerformanceQueryParameters';

export const PAGES_URL_SEARCH_DEBOUNCE_MS = 400;
/** The BFF 400s on a shorter `url_contains`. */
export const PAGES_URL_SEARCH_MIN_LENGTH = 3;

export type PagesUrlSearch = {
  /** What the input shows — possibly shorter than the minimum, possibly not yet in the URL. */
  inputValue: string;
  setInputValue: (text: string) => void;
  /** What goes to the BFF: the URL value, trimmed, or `''` under the minimum. */
  committed: string;
  /** The current URL search was typed here, not set by Review, a deep link or history. */
  wasTyped: boolean;
  /** Empties the input and the URL. Pushes history: it is a deliberate click. */
  clear: () => void;
};

function committedUrlSearch(text: string): string {
  const trimmed = text.trim();
  return trimmed.length >= PAGES_URL_SEARCH_MIN_LENGTH ? trimmed : '';
}

type UrlSearchOrigin = {
  value: string;
  typed: boolean;
};

/**
 * The Pages URL search (`?search=`): the URL owns it, the input is a debounced
 * editor of it. External URL changes (Review, history, the property picker) are
 * mirrored into the input; the hook's own writes are not, so keystrokes landing
 * while a write commits are kept.
 */
export function usePagesUrlSearch(): PagesUrlSearch {
  const { queryStrings, updateMultipleParameters } = useQueryStringParameters<SearchPerformanceQueryParams>(
    buildSearchPerformanceQueryParameters
  );
  const urlSearch = queryStrings.search ?? '';

  const [inputValue, setInputValue] = useState(urlSearch);

  // The value the last debounced write navigated to. A URL change equal to it
  // is that write landing; anything else is external. Consumed on landing (in
  // an effect, so a double-invoked render reads the same value twice) so a
  // later external change to the same text is not mistaken for our own.
  const pendingWriteRef = useRef<string | null>(null);
  const [origin, setOrigin] = useState<UrlSearchOrigin>({ value: urlSearch, typed: false });
  if (origin.value !== urlSearch) {
    const typed = pendingWriteRef.current === urlSearch;
    setOrigin({ value: urlSearch, typed });
    if (!typed) setInputValue(urlSearch);
  }
  useEffect(() => {
    pendingWriteRef.current = null;
  }, [urlSearch]);

  // The picker drops `search` from the URL on a switch, which is a no-op when
  // the text is still only in the input: the debounce would then write it
  // against the new property.
  const propertyScope = queryStrings.property ?? '';
  const [scope, setScope] = useState(propertyScope);
  if (scope !== propertyScope) {
    setScope(propertyScope);
    setInputValue(urlSearch);
  }

  useEffect(() => {
    const next = inputValue.trim();
    if (next === urlSearch.trim()) return undefined;
    const timer = setTimeout(() => {
      pendingWriteRef.current = next;
      updateMultipleParameters(
        { search: next === '' ? undefined : next },
        { filterNullish: true, resetPagination: true, replace: true }
      );
    }, PAGES_URL_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [inputValue, urlSearch, updateMultipleParameters]);

  const clear = () => {
    setInputValue('');
    // Not marked as our own write: the input is already empty, and a cleared
    // search is not a typed one.
    if (urlSearch !== '') updateMultipleParameters({ search: undefined });
  };

  return {
    inputValue,
    setInputValue,
    committed: committedUrlSearch(urlSearch),
    wasTyped: origin.typed,
    clear,
  };
}
